/**
 * Config-driven registry for the "spec option list" asset catalog modules.
 *
 * RAM, Disk Space, MS Office, SW Provider, CPU, Processor, and OS Version are all
 * backed by the same `categories` table in actionlabs-services, scoped to a fixed
 * parent_id per type (see App\Support\Sima\CategoryParentResolver on the backend).
 * Because the shape (name, slug, descriptions, active) and API surface
 * (/master-data/{type}) are identical for all seven, they share one list page,
 * one create page, one edit page, and one form component, parameterized by the
 * `:specType` route param instead of being duplicated seven times.
 *
 * To add a new spec type in the future: add an entry here, then add the matching
 * controller/service/migration on the backend (or reuse CategoryParentResolver if
 * it already exists) — no new frontend files needed.
 */
export interface SpecOptionTypeConfig {
  /** Route param value, e.g. /asset-configuration/spec/ram */
  slug: string
  /** API base path on actionlabs-services, e.g. /master-data/ram */
  apiBase: string
  /** Singular display label, e.g. "RAM" */
  label: string
  /** Plural display label, e.g. "RAM Options" */
  pluralLabel: string
  /** Placeholder example for the Name field */
  namePlaceholder: string
}

export const SPEC_OPTION_TYPES: Record<string, SpecOptionTypeConfig> = {
  ram: {
    slug: 'ram',
    apiBase: '/master-data/ram',
    label: 'RAM',
    pluralLabel: 'RAM Options',
    namePlaceholder: 'e.g. 8GB DDR4',
  },
  'disk-space': {
    slug: 'disk-space',
    apiBase: '/master-data/disk-space',
    label: 'Disk Space',
    pluralLabel: 'Disk Space Options',
    namePlaceholder: 'e.g. 256GB SSD',
  },
  'ms-office': {
    slug: 'ms-office',
    apiBase: '/master-data/ms-office',
    label: 'MS Office',
    pluralLabel: 'MS Office Options',
    namePlaceholder: 'e.g. MS Office 2021',
  },
  'sw-provider': {
    slug: 'sw-provider',
    apiBase: '/master-data/sw-provider',
    label: 'Software Provider',
    pluralLabel: 'Software Providers',
    namePlaceholder: 'e.g. Ingenico',
  },
  cpu: {
    slug: 'cpu',
    apiBase: '/master-data/cpu',
    label: 'CPU',
    pluralLabel: 'CPU Options',
    namePlaceholder: 'e.g. Quad-Core 2.4GHz',
  },
  processor: {
    slug: 'processor',
    apiBase: '/master-data/processor',
    label: 'Processor',
    pluralLabel: 'Processor Options',
    namePlaceholder: 'e.g. Intel Core i7',
  },
  'os-version': {
    slug: 'os-version',
    apiBase: '/master-data/os-version',
    label: 'OS Version',
    pluralLabel: 'OS Version Options',
    namePlaceholder: 'e.g. Windows 10 Pro',
  },
}

export function getSpecOptionType(slug: string | undefined): SpecOptionTypeConfig | null {
  if (!slug) return null
  return SPEC_OPTION_TYPES[slug] ?? null
}
